import React, { useEffect, useState, useCallback } from "react";
import { getDroneSoftware } from "../../services/DroneServices";
import { Link, useLocation } from "react-router-dom";
import { CloseButton, Col, Row } from "react-bootstrap";
import categoryIcon from "../../assets/image/svg/categoriesIcon.svg";
import priceFilterIcon from "../../assets/image/svg/priceFilterIcon.svg";
import Slider from "rc-slider";
import { getCategoryWithSubCategory } from "../../services/DronePartService";
import SearchIcon from "../../assets/image/svg/search_icon.svg";
import debounce from "lodash/debounce";
import "rc-slider/assets/index.css"; // Import slider CSS
import madeinindia from "../../assets/image/png/Madeinindia2.png";

const DroneParts = ({ selectedClassification }) => {
  // State variables
  const [allDroneParts, setAllDroneParts] = useState([]);
  const [droneParts, setDroneParts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [categoryData, setCategoryData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isOpen, setIsOpen] = useState([]);
  const [search, setSearch] = useState("");
  const [priceRange, setPriceRange] = useState([100, 500000]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortOption, setSortOption] = useState("popularity");
  const [minPrice, setMinPrice] = useState(100);
  const [maxPrice, setMaxPrice] = useState(500000);
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [dateAdded, setDateAdded] = useState("");
  const [specKeyword, setSpecKeyword] = useState("");
  const [madeInIndiaOnly, setMadeInIndiaOnly] = useState(false);

  const location = useLocation();

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearch(query);
      setAllDroneParts([]); // Reset drone parts for new search
      setPage(1); // Reset to the first page
    }, 0),
    [],
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };
  const handleDateChange = (e) => {
    setDateAdded(e.target.value); // Just store the string value directly
  };

  // Effect to set search term from URL query params
  // useEffect(() => {
  //   const urlParams = new URLSearchParams(location.search);
  //   console.log(location.search)
  //   const classification = urlParams.get('classification');
  //   if (classification) {
  //     setSearch(classification);
  //     // Set the classification name as the search term
  //     setPage(1); // Reset to the first page whenever classification changes
  //   }
  // }, [location.search]); // Re-run when the URL changes

  // Effect to update search term when selectedClassification changes
  // useEffect(() => {
  //   if (selectedClassification) {
  //     setSearch(selectedClassification); // Set search input to the selected classification
  //     setPage(1); // Reset to page 1 when classification changes
  //   }
  // }, [selectedClassification]);

  // Fetch drone parts whenever page, selectedCategory, or search changes
  useEffect(() => {
    const fetchDroneSoftware = async () => {
      try {
        const res = await getDroneSoftware({
          limit: 2000,
          categoryId: selectedCategory,
          search: search || "",
        });

        if (res.status === "success") {
          // If page is 1, reset. Else append
          if (page === 1) {
            setAllDroneParts(res.data.data);
          } else {
            setAllDroneParts((prev) => [...prev, ...res.data.data]);
          }
          setTotalPage(res.data.totalPages || 1);
        } else {
          console.error("Failed to fetch Plans:", res.message);
        }
      } catch (error) {
        console.error("Error fetching Plans:", error);
      }
    };

    // Only fetch if search term is set (even empty string)
    if (search !== undefined) {
      fetchDroneSoftware();
    }
  }, [page, selectedCategory, search]);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getCategoryWithSubCategory();
        setCategoryData(res?.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Initialize isOpen state based on categoryData length
  useEffect(() => {
    setIsOpen(Array(categoryData?.length).fill(false));
  }, [categoryData]);

  // Filter and sort drone parts whenever relevant states change
  useEffect(() => {
    const filterByPriceBrandAndSort = () => {
      let filteredParts = allDroneParts
        .filter(
          (part) =>
            part.discountPrice >= priceRange[0] &&
            part.discountPrice <= priceRange[1],
        )
        .filter((part) => !selectedBrand || part.brand === selectedBrand)
        .filter(
          (part) =>
            !selectedManufacturer || part.manufacturer === selectedManufacturer,
        )
        // .filter(part => !inStockOnly || !part.outOfStock)
        .filter((part) => !madeInIndiaOnly || part.madeInIndia)
        .filter((part) => {
          if (!dateAdded) return true; // If no date filter is set, include all parts

          // Ensure the part has a createdAt field
          if (!part.createdAt) return false; // Exclude parts without a creation date

          // Convert both dates to Date objects and compare
          const partDate = new Date(part.createdAt);
          const filterDate = new Date(dateAdded);

          // Set time to 00:00:00 for accurate day comparison
          partDate.setHours(0, 0, 0, 0);
          filterDate.setHours(0, 0, 0, 0);

          return partDate >= filterDate;
        })
        .filter(
          (part) =>
            !specKeyword ||
            (part.specifications &&
              part.specifications
                .toLowerCase()
                .includes(specKeyword.toLowerCase())),
        );

      switch (sortOption) {
        case "priceAsc":
          filteredParts.sort((a, b) => a.discountPrice - b.discountPrice);
          break;
        case "priceDesc":
          filteredParts.sort((a, b) => b.discountPrice - a.discountPrice);
          break;
        case "popularity":
          filteredParts.sort((a, b) => b.popularity - a.popularity);
          break;
        case "newest":
          filteredParts.sort(
            (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded),
          );
          break;
        default:
          filteredParts.sort((a, b) => b.popularity - a.popularity);
          break;
      }

      setDroneParts(filteredParts);
    };

    filterByPriceBrandAndSort();
  }, [
    priceRange,
    allDroneParts,
    selectedBrand,
    sortOption,
    selectedManufacturer,
    // inStockOnly,
    dateAdded,
    specKeyword,
    madeInIndiaOnly,
  ]);

  // Clear all filters
  const handleClearFilter = async () => {
    setSelectedCategory(null);
    setSearch("");
    setSelectedBrand(null);
    setPriceRange([100, 500000]);
    setSortOption("popularity");
    setPage(1);
    setMadeInIndiaOnly(false);
    setDateAdded(null);

    try {
      const res = await getDroneSoftware({
        limit: 2000,
        limit: 2000,
        categoryId: null,
        search: "",
        // page: 1
      });

      if (res.status === "success") {
        setAllDroneParts(res.data.data);
        setTotalPage(res.data.totalPages || 1);
      } else {
        console.error("Failed to fetch data:", res.message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Handle min price change
  const handleMinPriceChange = (e) => {
    const value = parseInt(e.target.value);
    if (value <= maxPrice) {
      setMinPrice(value);
      setPriceRange([value, maxPrice]);
    }
  };

  // Handle max price change
  const handleMaxPriceChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= minPrice) {
      setMaxPrice(value);
      setPriceRange([minPrice, value]);
    }
  };

  // Price options for dropdowns
  const priceOptions = [
    100, 500, 1000, 5000, 10000, 20000, 30000, 40000, 50000, 60000, 70000,
    80000, 90000, 100000, 150000, 200000, 300000, 400000, 500000,
  ];

  return (
    <div className="py-5">
      {showFilters && (
        <div className="overlay" onClick={() => setShowFilters(false)}></div>
      )}
      <div className="container">
        <div className="d-flex align-items-center justify-content-between sticky top-4 -z-10">
          {/* <button onClick={() => setShowFilters(true)} className="d-lg-none btn btn-light p-2 rounded">
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button> */}
          <h3
            className="fs_43 text-lg-start text-center margin_custom mx-2"
            style={{ paddingBottom: "5px" }}
          >
            SaaS{" "}
            {/* <span style={{ color: "#328CCD" }}>Drone Solutions</span> */}
          </h3>
        </div>

        <Row className="flex-lg-row">
          <Col lg={3} className="mb-4">
            {/* Filter Sidebar */}
            <div className="sticky top-28">
              {/* Horizontal scroll container for mobile */}
              <div className="lg:hidden overflow-x-auto pb-2 mb-4 hide-scrollbar">
                <div className="w-max flex space-x-2 px-2">
                  {/* Mobile Filter Chips */}
                  <button
                    onClick={() => setMadeInIndiaOnly(!madeInIndiaOnly)}
                    className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${madeInIndiaOnly ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}
                  >
                    Made in India
                  </button>
                  {/* <button 
          onClick={() => setInStockOnly(!inStockOnly)}
          className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${inStockOnly ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          In Stock
        </button> */}
                  <button
                    onClick={handleClearFilter}
                    className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 whitespace-nowrap"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {/* Vertical scroll container for desktop */}
              <div className="hidden lg:block h-[calc(100vh-140px)] overflow-y-auto">
                <div className="bg-white rounded-lg shadow-sm p-4">
                  {/* Search Box */}
                  <div className="mb-4">
                    <form
                      onSubmit={(e) => e.preventDefault()}
                      className="relative"
                    >
                      <input
                        type="text"
                        value={search}
                        onChange={handleSearchChange}
                        placeholder="Search Plans"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <img
                          src={SearchIcon}
                          alt="Search"
                          className="w-5 h-5 text-gray-400"
                        />
                      </div>
                    </form>
                  </div>

                  {/* Price Range Filter */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-base font-medium text-gray-800">
                        Price Range
                      </h4>
                    </div>

                    <Slider
                      range
                      allowCross={false}
                      min={100}
                      max={500000}
                      value={priceRange}
                      onChange={setPriceRange}
                      className="mb-4"
                      trackStyle={{ backgroundColor: "#008CFF" }}
                    />

                    <div className="flex justify-between text-sm text-gray-600 mb-3">
                      <span>₹{priceRange[0].toLocaleString("en-IN")}</span>
                      <span>₹{priceRange[1].toLocaleString("en-IN")}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Min Price
                        </label>
                        <select
                          value={minPrice}
                          onChange={handleMinPriceChange}
                          className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        >
                          {priceOptions.map((price) => (
                            <option key={price} value={price}>
                              ₹{price.toLocaleString("en-IN")}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Max Price
                        </label>
                        <select
                          value={maxPrice}
                          onChange={handleMaxPriceChange}
                          className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        >
                          {priceOptions.map((price) => (
                            <option key={price} value={price}>
                              ₹{price.toLocaleString("en-IN")}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Additional Filters */}
                  <div className="space-y-4">
                    {/* In-Stock Toggle */}
                    {/* <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <label htmlFor="inStockOnlyCheck" className="text-sm font-medium text-gray-700 cursor-pointer">
              In Stock Only
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                id="inStockOnlyCheck"
                className="sr-only peer"
                checked={inStockOnly}
                onChange={() => setInStockOnly(!inStockOnly)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div> */}

                    {/* Made in India Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <label
                        htmlFor="madeInIndiaCheck"
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                      >
                        Made in India Only
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="madeInIndiaCheck"
                          className="sr-only peer"
                          checked={madeInIndiaOnly}
                          onChange={() => setMadeInIndiaOnly(!madeInIndiaOnly)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Date Added Filter */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Added On or After
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-md p-2 text-sm"
                        value={dateAdded || ""}
                        onChange={handleDateChange}
                        max={new Date().toISOString().split("T")[0]}
                      />
                    </div>

                    {/* Specification Keyword Filter */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Specification Keywords
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-md p-2 text-sm"
                        placeholder="e.g. 2200KV, 11.1V"
                        value={specKeyword}
                        onChange={(e) => setSpecKeyword(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={9}>
            <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
              <div className="flex flex-wrap items-center gap-3 mt-0">
                <p className="mb-0 font-bold text-sm">Sort By:</p>
                <button
                  className={`px-3 py-1 text-sm rounded-md border ${
                    sortOption === "priceAsc"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                  onClick={() => setSortOption("priceAsc")}
                >
                  Price -- Low to High
                </button>
                <button
                  className={`px-3 py-1 text-sm rounded-md border ${
                    sortOption === "priceDesc"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                  onClick={() => setSortOption("priceDesc")}
                >
                  Price -- High to Low
                </button>
                <button
                  className={`px-3 py-1 text-sm rounded-md border ${
                    sortOption === "newest"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                  onClick={() => setSortOption("newest")}
                >
                  Newest First
                </button>
              </div>
            </div>

            <div className="row pb-md-4 bg-white">
              {droneParts.map((item, index) => (
                <div
                  key={index}
                  className="col-lg-4 cursor_pointer col-sm-6 col-md-4 mt-4 col-12"
                >
                  {item.outOfStock ? (
                    <div className="product-card border d-flex flex-column justify-content-between border-1 h-100 rounded-3 position-relative">
                      {/* Out of Stock Overlay */}
                      <div className="position-absolute top-0 start-0 w-100 h-100 bg-light opacity-50"></div>

                      {/* Out of Stock Badge */}
                      <div
                        className="position-absolute top-0 start-0  d-flex align-items-center px-3 py-1"
                        style={{
                          background:
                            "linear-gradient(135deg, #b00020, #ff1744)",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "14px",
                          height: "32px",
                          borderTopLeftRadius: "12px",
                          borderBottomRightRadius: "12px",
                        }}
                      >
                        Out of Stock
                      </div>

                      {/* Made in India Badge */}
                      {item.madeInIndia && (
                        <img
                          src={madeinindia}
                          alt="Made in India"
                          style={{
                            position: "absolute",
                            left: "10px",
                            top: "10px",
                            height: "40px",
                            zIndex: 3,
                          }}
                        />
                      )}

                      <div>
                        <img
                          height={170}
                          className="mx-auto w-100 object-fit-cover w_xsm_full rounded-3"
                          src={item.images[0]}
                          alt="drone"
                          loading="lazy"
                          style={{ opacity: 0.7 }}
                        />
                        <p className="px-3 mt-3 fs_15 fw-bold ff_inter color_darkgray mb-0">
                          {item?.title.length > 50
                            ? `${item.title.substring(0, 50)}...`
                            : item.title}
                        </p>
                      </div>
                      <div className="mt-3 px-3">
                        <div className="mt-3 d-flex justify-content-between mb-3 align-items-center">
                          <div className="d-flex flex-column">
                            <span className="fs_15 ff_inter font-bold">
                              Rs. {item.price.toLocaleString("en-IN")}
                            </span>
                          </div>
                          <button
                            className="viewall_btn bg-secondary px-4 py-2 fs_17 fw-medium ff_inter text-white text-nowrap"
                            disabled
                          >
                            Out of Stock
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link to={`/drone-software-details/${item._id}`}>
                      <div className="product-card border d-flex flex-column justify-content-between border-1 h-100 rounded-3 position-relative">
                        {/* Made in India Badge */}
                        {item.madeInIndia && (
                          <img
                            src={madeinindia}
                            alt="Made in India"
                            style={{
                              position: "absolute",
                              left: "10px",
                              top: "10px",
                              height: "40px",
                              zIndex: 2,
                            }}
                          />
                        )}

                        <div>
                          <img
                            height={170}
                            className="mx-auto w-100 object-fit-contain w_xsm_full rounded-3"
                            src={item.images[0]}
                            alt="drone"
                            loading="lazy"
                          />
                          <p className="px-3 mt-3 fs_15 fw-bold ff_inter color_darkgray mb-0">
                            {item?.title.length > 50
                              ? `${item.title.substring(0, 50)}...`
                              : item.title}
                          </p>
                        </div>
                        <div className="mt-3 px-3">
                          <div className="mt-3 d-flex justify-content-between mb-3 align-items-center">
                            <div className="d-flex flex-column">
                              <span className="fs_15 ff_inter font-bold">
                                Rs. {item.price.toLocaleString("en-IN")}
                              </span>
                            </div>
                            <button className="viewall_btn bg_light_blue px-3 py-2 fs_17 fw-medium ff_inter text-white text-nowrap">
                              Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
              ))}

              {/* No Data Found */}
              {droneParts.length === 0 && (
                <p className="fw-bold fs-4 text-center mt-5 pt-5 ff_inter">
                  No Data Found
                </p>
              )}

              {/* Load More Button */}
              {page !== totalPage && (
                <div className="d-flex justify-content-center">
                  <button
                    onClick={() => setPage(page + 1)}
                    className="tab_btn px-4 py-2 border-0 rounded-2 mt-5 fs_13 ff_inter fw-semibold text-white"
                  >
                    Load More
                  </button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DroneParts;
